import { computed, inject, Injectable, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { EnvironmentService } from '@nomoh-monorepo/shared-data-access-environment';
import { firstValueFrom } from 'rxjs';
import { IPagerSettings, StatusHistorico } from '@nomoh-monorepo/shared-types';
import {
  IPlanoHistorico,
  IRelatorioHistoricoFiltro,
  IVincularPlano,
  ICancelarPlano,
  IDesvincularPlano,
  IPlanoHistoricoResponse,
} from '@nomoh-monorepo/shared-types';

@Injectable({ providedIn: 'root' })
export class PlanoHistoricosDataService {
  private http = inject(HttpClient);
  private env = inject(EnvironmentService);
  private baseUrl = `${this.env.getApiUrl('nomoh')}/plano-historico`;

  // ========================================
  // SIGNALS PRIVADOS (estado interno)
  // ========================================
  private _historicos = signal<IPlanoHistorico[]>([]);
  private _historicoSelecionado = signal<IPlanoHistorico | null>(null);
  private _loading = signal<boolean>(false);
  private _erro = signal<string | null>(null);
  private _paginacao = signal<IPagerSettings>({
    total: 0,
    offSet: 0,
    limit: 10,
    param: '',
    ordenacao: 0,
  });
  private _filtros = signal<Omit<IRelatorioHistoricoFiltro, 'page' | 'limit'>>({
    pessoaId: undefined,
    pessoaNome: undefined,
    planoId: undefined,
    status: undefined,
    dataInicio: undefined,
    dataFim: undefined,
    sistemaId: undefined,
  });
  private _historicosDetalhe = signal<IPlanoHistorico[]>([]);
  private _loadingDetalhe = signal<boolean>(false);

  // ========================================
  // SIGNALS PÚBLICOS (readonly)
  // ========================================
  $historicos = this._historicos.asReadonly();
  $historicoSelecionado = this._historicoSelecionado.asReadonly();
  $historicosDetalhe = this._historicosDetalhe.asReadonly();
  $loading = this._loading.asReadonly();
  $loadingDetalhe = this._loadingDetalhe.asReadonly();
  $erro = this._erro.asReadonly();
  $paginacao = this._paginacao.asReadonly();
  $filtros = this._filtros.asReadonly();

  // ========================================
  // COMPUTED SIGNALS (estado derivado)
  // ========================================
  $temProxPagina = computed(() => {
    const pag = this.$paginacao();
    const offSet = pag.offSet ?? 0;
    const limit = pag.limit ?? 10;
    const total = pag.total ?? 0;
    return offSet + limit < total;
  });

  $temPagAnterior = computed(() => {
    const offSet = this.$paginacao().offSet ?? 0;
    return offSet > 0;
  });

  $paginaAtual = computed(() => {
    const pag = this.$paginacao();
    const offSet = pag.offSet ?? 0;
    const limit = pag.limit ?? 10;
    return Math.floor(offSet / limit) + 1;
  });

  $totalPaginas = computed(() => {
    const pag = this.$paginacao();
    const total = pag.total ?? 0;
    const limit = pag.limit ?? 10;
    return Math.ceil(total / limit);
  });

  $infoTotal = computed(() => {
    const pag = this.$paginacao();
    const offSet = pag.offSet ?? 0;
    const limit = pag.limit ?? 10;
    const total = pag.total ?? 0;
    const inicio = offSet + 1;
    const fim = Math.min(offSet + limit, total);
    return `${inicio}-${fim} de ${total}`;
  });

  $hasHistoricos = computed(() => this.$historicos().length > 0);
  $isEmpty = computed(
    () => !this.$loading() && this.$historicos().length === 0
  );

  $historicosAtivos = computed(() =>
    this.$historicos().filter((h) => h.status === StatusHistorico.ATIVO)
  );

  $historicosCancelados = computed(() =>
    this.$historicos().filter((h) => h.status === StatusHistorico.CANCELADO)
  );

  $historicosExpirados = computed(() =>
    this.$historicos().filter((h) => h.status === StatusHistorico.EXPIRADO)
  );

  // ========================================
  // MÉTODOS DE ESTADO
  // ========================================
  setHistoricos(response: IPlanoHistoricoResponse) {
    this._historicos.set(response.data);
    this._paginacao.update((current) => ({
      ...current,
      total: response.total,
    }));
  }

  setLoading(loading: boolean) {
    this._loading.set(loading);
  }

  setErro(erro: string | null) {
    this._erro.set(erro);
  }

  setFiltros(
    filtros: Partial<Omit<IRelatorioHistoricoFiltro, 'page' | 'limit'>>
  ) {
    this._filtros.set({
      pessoaId: undefined,
      pessoaNome: undefined,
      planoId: undefined,
      status: undefined,
      dataInicio: undefined,
      dataFim: undefined,
      sistemaId: undefined,
      ...filtros,
    });
  }

  selecionarHistorico(historico: IPlanoHistorico | null): void {
    this._historicoSelecionado.set(historico);
  }

  limparErro(): void {
    this._erro.set(null);
  }

  // ========================================
  // OPERAÇÕES HTTP + ESTADO
  // ========================================

  /**
   * Gera relatório com filtros e paginação
   */
  async gerarRelatorio(opcoes?: {
    offSet?: number;
    limit?: number;
    filtros?: Partial<Omit<IRelatorioHistoricoFiltro, 'page' | 'limit'>>;
  }): Promise<void> {
    this.setLoading(true);
    this.setErro(null);

    try {
      const pag = this.$paginacao();
      const offSet = opcoes?.offSet ?? pag.offSet ?? 0;
      const limit = opcoes?.limit ?? pag.limit ?? 10;
      const page = Math.floor(offSet / limit) + 1;

      // Atualizar filtros se fornecidos (substitui, não faz merge)
      if (opcoes?.filtros !== undefined) {
        this.setFiltros(opcoes.filtros);
      }

      const filtrosLimpos = Object.fromEntries(
        Object.entries(this.$filtros()).filter(
          ([, value]) => value !== undefined && value !== null && value !== ''
        )
      );

      const filtroCompleto: IRelatorioHistoricoFiltro = {
        ...filtrosLimpos,
        page,
        limit,
      };

      const response = await firstValueFrom(
        this.http.post<IPlanoHistoricoResponse>(
          `${this.baseUrl}/relatorio`,
          filtroCompleto
        )
      );

      this.setHistoricos(response);
      this._paginacao.update((current) => ({
        ...current,
        offSet,
        limit,
      }));
    } catch (error: any) {
      const mensagem = error.message || 'Erro ao gerar relatório';
      this.setErro(mensagem);
      throw error;
    } finally {
      this.setLoading(false);
    }
  }

  /**
   * Busca histórico por pessoa
   */
  async buscarPorPessoa(pessoaId: string): Promise<void> {
    this.setLoading(true);
    this.setErro(null);

    try {
      const historicos = await firstValueFrom(
        this.http.get<IPlanoHistorico[]>(`${this.baseUrl}/pessoa/${pessoaId}`)
      );
      this._historicos.set(historicos);
      this._paginacao.update((current) => ({
        ...current,
        total: historicos.length,
      }));
    } catch (error: any) {
      const mensagem = error.message || 'Erro ao buscar histórico';
      this.setErro(mensagem);
      throw error;
    } finally {
      this.setLoading(false);
    }
  }

  /**
   * Carrega o histórico completo de uma pessoa sem alterar a listagem
   */
  async carregarDetalhesPessoa(pessoaId: string): Promise<void> {
    this._loadingDetalhe.set(true);
    this.setErro(null);

    try {
      const body = await firstValueFrom(
        this.http.get<IPlanoHistorico[] | { data: IPlanoHistorico[] }>(
          `${this.baseUrl}/pessoa/${pessoaId}`
        )
      );
      const historicos = Array.isArray(body) ? body : body?.data ?? [];
      this._historicosDetalhe.set(historicos);
    } catch (error: any) {
      const mensagem = error.message || 'Erro ao buscar histórico da pessoa';
      this.setErro(mensagem);
      this._historicosDetalhe.set([]);
      throw error;
    } finally {
      this._loadingDetalhe.set(false);
    }
  }

  limparDetalhesPessoa(): void {
    this._historicosDetalhe.set([]);
  }

  /**
   * Vincula plano a uma pessoa
   */
  async vincularPlano(data: IVincularPlano): Promise<string> {
    this.setLoading(true);
    this.setErro(null);

    try {
      const response = await firstValueFrom(
        this.http.post<{ id: string }>(`${this.baseUrl}/vincular`, data)
      );
      const id = response.id;

      // Recarregar lista após vinculação
      await this.gerarRelatorio();

      return id;
    } catch (error: any) {
      const mensagem = error.message || 'Erro ao vincular plano';
      this.setErro(mensagem);
      throw error;
    } finally {
      this.setLoading(false);
    }
  }

  /**
   * Cancela plano manualmente
   */
  async cancelarPlano(data: ICancelarPlano): Promise<void> {
    this.setLoading(true);
    this.setErro(null);

    try {
      await firstValueFrom(
        this.http.post<{ message: string }>(`${this.baseUrl}/cancelar`, data)
      );

      // Recarregar lista após cancelamento
      await this.gerarRelatorio();
    } catch (error: any) {
      const mensagem = error.message || 'Erro ao cancelar plano';
      this.setErro(mensagem);
      throw error;
    } finally {
      this.setLoading(false);
    }
  }

  /**
   * Desvincula plano ativo de um usuário
   */
  async desvincularPlano(data: IDesvincularPlano): Promise<void> {
    this.setLoading(true);
    this.setErro(null);

    try {
      await firstValueFrom(
        this.http.post<{ message: string }>(`${this.baseUrl}/desvincular`, data)
      );

      // Não recarregar lista aqui - deixar o componente fazer
    } catch (error: any) {
      const mensagem = error.message || 'Erro ao desvincular plano';
      this.setErro(mensagem);
      throw error;
    } finally {
      this.setLoading(false);
    }
  }

  // ========================================
  // MÉTODOS DE PAGINAÇÃO
  // ========================================

  async proximaPagina(): Promise<void> {
    if (!this.$temProxPagina()) return;

    const pag = this.$paginacao();
    const novoOffSet = (pag.offSet ?? 0) + (pag.limit ?? 10);

    await this.gerarRelatorio({ offSet: novoOffSet });
  }

  async paginaAnterior(): Promise<void> {
    if (!this.$temPagAnterior()) return;

    const pag = this.$paginacao();
    const novoOffSet = Math.max(0, (pag.offSet ?? 0) - (pag.limit ?? 10));

    await this.gerarRelatorio({ offSet: novoOffSet });
  }

  async irParaPagina(pagina: number): Promise<void> {
    const limit = this.$paginacao().limit ?? 10;
    const offSet = (pagina - 1) * limit;

    await this.gerarRelatorio({ offSet });
  }

  async alterarTamanhoPagina(limit: number): Promise<void> {
    await this.gerarRelatorio({ offSet: 0, limit });
  }

  // ========================================
  // RESET STATE
  // ========================================
  resetState(): void {
    this._historicos.set([]);
    this._historicoSelecionado.set(null);
    this._historicosDetalhe.set([]);
    this._loading.set(false);
    this._loadingDetalhe.set(false);
    this._erro.set(null);
    this._paginacao.set({
      total: 0,
      offSet: 0,
      limit: 10,
      param: '',
      ordenacao: 0,
    });
    this._filtros.set({
      pessoaId: undefined,
      pessoaNome: undefined,
      planoId: undefined,
      status: undefined,
      dataInicio: undefined,
      dataFim: undefined,
      sistemaId: undefined,
    });
  }

  /**
   * Limpa apenas os filtros (mantém paginação e dados)
   */
  limparFiltros(): void {
    this._filtros.set({
      pessoaId: undefined,
      pessoaNome: undefined,
      planoId: undefined,
      status: undefined,
      dataInicio: undefined,
      dataFim: undefined,
      sistemaId: undefined,
    });
  }
}
